import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send, Clock, User as UserIcon, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { MainLayout } from '../layouts';
import { api } from '../lib/api';
import { ChatHistory } from '../components/ChatHistory';
import { WhatsAppRichTextEditor } from '../components/WhatsAppRichTextEditor';

export const EscalationDetail = () => {
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);
  const { escalationId } = useParams<{ escalationId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [responseMessage, setResponseMessage] = useState('');

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Fetch escalation details
  const { data: escalation, isLoading: escalationLoading } = useQuery({
    queryKey: ['escalation', escalationId],
    queryFn: () => api.getEscalation(escalationId!),
    enabled: !!escalationId,
  });

  // Chat context is fetched by ChatHistory component directly

  // Send response mutation
  const sendResponseMutation = useMutation({
    mutationFn: async (message: string) => {
      if (!escalationId) throw new Error('Escalation ID not found');
      return api.sendExpertResponse(escalationId, message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escalations'] });
      queryClient.invalidateQueries({ queryKey: ['escalation', escalationId] });
      navigate('/expert-dashboard', { 
        replace: true,
        state: { message: 'Response sent successfully!' }
      });
    },
    onError: (error) => {
      alert(`Failed to send response: ${error.message}`);
    },
  });

  const handleSendResponse = () => {
    const trimmedMessage = responseMessage.trim();
    if (!trimmedMessage) {
      alert('Please enter a response message');
      return;
    }
    sendResponseMutation.mutate(trimmedMessage);
  };

  const handleBack = () => {
    navigate('/expert-dashboard');
  };

  // Check if user can respond to this escalation
  const canRespond = escalation && 
    escalation.status !== 'resolved' && 
    (!escalation.assignedExpertId || escalation.assignedExpertId === currentUser.id);

  const isAssignedToOther = escalation && 
    escalation.status === 'in_progress' && 
    escalation.assignedExpertId && 
    escalation.assignedExpertId !== currentUser.id && 
    currentUser.roles?.includes('expert');

  // Loading skeleton
  if (escalationLoading) {
    return (
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Top Bar Skeleton */}
        <div className="bg-white border-b border-gray-200 p-4 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-8 w-8 bg-gray-200 rounded" />
              <div className="h-6 w-48 bg-gray-200 rounded" />
            </div>
            <div className="flex gap-2">
              <div className="h-6 w-20 bg-gray-200 rounded-full" />
              <div className="h-6 w-20 bg-gray-200 rounded-full" />
            </div>
          </div>
        </div>
        
        {/* Chat Area Skeleton */}
        <div className="flex-1 bg-white animate-pulse p-6">
          <div className="space-y-4">
            <div className="h-16 bg-gray-200 rounded" />
            <div className="h-16 bg-gray-200 rounded" />
            <div className="h-16 bg-gray-200 rounded" />
          </div>
        </div>

        {/* Editor Skeleton */}
        <div className="bg-white border-t border-gray-200 p-4 animate-pulse">
          <div className="h-32 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!escalation) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Escalation not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The escalation you're looking for doesn't exist or has been removed.
          </p>
          <div className="mt-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const priorityColors = {
    low: 'bg-gray-100 text-gray-800',
    normal: 'bg-blue-100 text-blue-800',
    high: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800',
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
  };

  return (
    <MainLayout noPadding>
      <div className="h-full flex flex-col bg-gray-50">
        {/* Compact Top Bar with Metadata */}
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
          {/* Main Top Bar */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <button
                onClick={handleBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back</span>
            </button>
            
            <div className="h-6 w-px bg-gray-300" />
            
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <UserIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {escalation.user?.displayName || 'Unknown'}
                </p>
                <p className="text-xs text-gray-500">{escalation.user?.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex gap-2">
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  priorityColors[escalation.priority]
                }`}
              >
                {escalation.priority.toUpperCase()}
              </span>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  statusColors[escalation.status]
                }`}
              >
                {escalation.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            
            <button
              onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title={isMetadataExpanded ? 'Hide details' : 'Show details'}
            >
              {isMetadataExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Expandable Metadata Section */}
        {isMetadataExpanded && (
          <div className="px-4 pb-3 border-t border-gray-100 bg-gray-50">
            <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-gray-500">Escalated</p>
                  <p className="text-sm text-gray-900">
                    {new Date(escalation.escalatedAt).toLocaleString()}
                  </p>
                </div>
              </div>

              {escalation.assignedExpert && (
                <div className="flex items-start gap-2">
                  <UserIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500">Assigned Expert</p>
                    <p className="text-sm text-gray-900">{escalation.assignedExpert.displayName}</p>
                  </div>
                </div>
              )}

              {escalation.status === 'resolved' && escalation.resolvedAt && (
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500">Resolved</p>
                    <p className="text-sm text-gray-900">
                      {new Date(escalation.resolvedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {escalation.reason && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1">Reason</p>
                <p className="text-sm text-gray-900">{escalation.reason}</p>
              </div>
            )}

            {isAssignedToOther && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800">
                  Currently being handled by <strong>{escalation.assignedExpert?.displayName}</strong>.
                  You can view but cannot respond.
                </p>
              </div>
            )}

            {escalation.status === 'resolved' && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-green-800 font-medium">
                  This escalation has been resolved
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat History - Full Height */}
      {escalation.user && escalationId && (
        <div className="flex-1 overflow-hidden">
          <ChatHistory
            user={escalation.user}
            escalationId={escalationId as string}
            isResolved={escalation.status === 'resolved'}
          />
        </div>
      )}

      {/* Sticky Bottom Response Editor */}
      <div className="bg-white border-t border-gray-200 flex-shrink-0">
        <div className="px-4 py-3">
          {escalation.status === 'resolved' ? (
            /* Read-only view for resolved escalations */
            <div className="flex items-center justify-center py-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3 text-green-800">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold">Escalation Resolved</p>
                  <p className="text-xs text-green-600">
                    Viewing complete conversation history in read-only mode
                    {escalation.resolvedAt && ` • Resolved ${new Date(escalation.resolvedAt).toLocaleString()}`}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Active response editor for pending/in-progress escalations */
            <>
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <WhatsAppRichTextEditor
                    value={responseMessage}
                    onChange={setResponseMessage}
                    placeholder="Type your response to the user..."
                    disabled={!canRespond || sendResponseMutation.isPending}
                  />
                </div>
                <button
                  onClick={handleSendResponse}
                  disabled={
                    !canRespond ||
                    !responseMessage.trim() ||
                    sendResponseMutation.isPending
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex-shrink-0 h-10"
                  title={
                    canRespond
                      ? 'Send response and resolve escalation'
                      : 'You cannot respond to this escalation'
                  }
                >
                  {sendResponseMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm font-medium">Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span className="text-sm font-medium">Send & Resolve</span>
                    </>
                  )}
                </button>
              </div>
              {canRespond && (
                <p className="text-xs text-gray-500 mt-2">
                  Response will be sent and automatically resolve this escalation
                </p>
              )}
            </>
          )}
        </div>
      </div>
      </div>
    </MainLayout>
  );
};
